import React, { useState } from 'react';
import { MapPin, Navigation, Copy, Check, ExternalLink, Clock, Route, Apple, Chrome } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

// Ranch location
const ADDRESS_STREET = '2724 Private Road 8160';
const ADDRESS_CITY = 'Lovelady, TX 75851';
const ADDRESS_FULL = `${ADDRESS_STREET}, ${ADDRESS_CITY}`;
// Encoded once so the multiple links stay consistent
const ADDRESS_ENC = encodeURIComponent(ADDRESS_FULL);

// Driving directions from surrounding cities.
// Times are approximate; distances are highway miles.
const CITIES = [
  {
    city: 'Houston, TX',
    time: '2 h 5 min',
    miles: '115 mi',
    route:
      'Take I-45 N about 90 mi to Huntsville. Continue on TX-19 N through Trinity to Crockett, then US-287 S to Lovelady. Turn onto FM-230, then Private Road 8160.',
  },
  {
    city: 'Huntsville, TX',
    time: '45 min',
    miles: '38 mi',
    route:
      'Head north on I-45 to Riverside. Take TX-19 N through Trinity to Crockett, then US-287 S to Lovelady. Turn onto FM-230, then Private Road 8160.',
  },
  {
    city: 'Crockett, TX',
    time: '15 min',
    miles: '11 mi',
    route:
      'From downtown Crockett, take US-287 S toward Lovelady. Turn onto FM-230, follow to Private Road 8160.',
  },
  {
    city: 'Lufkin, TX',
    time: '1 h 5 min',
    miles: '52 mi',
    route:
      'Take US-59 N briefly, then US-287 N through Groveton to Lovelady. Turn onto FM-230 to Private Road 8160.',
  },
  {
    city: 'Dallas, TX',
    time: '3 h 15 min',
    miles: '180 mi',
    route:
      'Take I-45 S to Buffalo. Continue south to Huntsville then TX-19 N to Crockett. US-287 S to Lovelady, then FM-230 to Private Road 8160.',
  },
  {
    city: 'College Station, TX',
    time: '1 h 40 min',
    miles: '95 mi',
    route:
      'Take TX-6 N to Madisonville, then TX-21 N/E toward Crockett. US-287 S to Lovelady, then FM-230 to Private Road 8160.',
  },
  {
    city: 'Livingston, TX',
    time: '1 h 5 min',
    miles: '55 mi',
    route:
      'Take US-59 N through Corrigan to Lufkin. US-287 N to Lovelady, then FM-230 to Private Road 8160.',
  },
  {
    city: 'Nacogdoches, TX',
    time: '1 h 30 min',
    miles: '75 mi',
    route:
      'Head west on TX-21 to Alto, then continue to Crockett. US-287 S to Lovelady, then FM-230 to Private Road 8160.',
  },
];

const googleMapsDirUrl = (from) =>
  `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(from)}&destination=${ADDRESS_ENC}`;

export const Directions = () => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS_FULL);
      setCopied(true);
      toast({ title: 'Address copied', description: ADDRESS_FULL });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast({ title: 'Copy failed', description: 'Please copy manually.', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      {/* Hero */}
      <section className="relative py-20 md:py-24 overflow-hidden border-b border-[#B8860B]/20">
        <div
          className="absolute inset-0 z-0 opacity-40"
          style={{
            backgroundImage:
              'radial-gradient(circle at 12% 20%, #E5C158 1px, transparent 2px), radial-gradient(circle at 60% 40%, #E6E8FA 1px, transparent 2px), radial-gradient(circle at 80% 75%, #D4AF37 1px, transparent 2px), radial-gradient(circle at 25% 80%, #ffffff 1px, transparent 2px)',
            backgroundSize: '300px 200px',
          }}
        />
        <div className="relative container mx-auto px-4 text-center">
          <MapPin className="mx-auto text-[#D4AF37] mb-4" size={44} strokeWidth={1.4} />
          <p className="uppercase tracking-[0.4em] text-[10px] md:text-xs text-[#D4AF37] mb-3">
            Find Your Way to Us
          </p>
          <h1 className="font-display text-4xl md:text-6xl font-medium text-[#F4F1EA] mb-4 tracking-tight">
            Take Me to the Ranch
          </h1>
          <p className="font-display text-xl md:text-2xl text-[#F4F1EA]/80 mb-6 italic">
            {ADDRESS_STREET}, {ADDRESS_CITY}
          </p>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
            <Button
              data-testid="directions-open-google-btn"
              asChild
              className="bg-[#B8860B] hover:bg-[#D4AF37] text-[#050814] font-semibold rounded-full px-6 shadow-[0_0_26px_rgba(184,134,11,0.35)]"
            >
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${ADDRESS_ENC}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Navigation size={16} className="mr-2" /> Open in Google Maps
              </a>
            </Button>
            <Button
              data-testid="directions-open-apple-btn"
              asChild
              variant="outline"
              className="bg-transparent border-[#B8860B] text-[#E5C158] hover:bg-[#B8860B]/10 hover:text-[#F4F1EA] font-medium rounded-full px-6"
            >
              <a
                href={`http://maps.apple.com/?daddr=${ADDRESS_ENC}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Apple size={16} className="mr-2" /> Open in Apple Maps
              </a>
            </Button>
            <Button
              data-testid="directions-copy-btn"
              onClick={handleCopy}
              variant="outline"
              className="bg-transparent border-[#B8860B]/40 text-[#F4F1EA]/80 hover:bg-[#B8860B]/10 hover:text-[#E5C158] font-medium rounded-full px-6"
            >
              {copied ? (
                <>
                  <Check size={16} className="mr-2" /> Copied
                </>
              ) : (
                <>
                  <Copy size={16} className="mr-2" /> Copy Address
                </>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Map + arrival tips */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Map */}
            <div className="lg:col-span-2">
              <Card
                data-testid="directions-map-card"
                className="border border-[#B8860B]/30 bg-[#0B132B] overflow-hidden shadow-xl"
              >
                <div className="aspect-video w-full">
                  <iframe
                    data-testid="directions-map-iframe"
                    title="Creek Bottom Ranch — Google Map"
                    src={`https://maps.google.com/maps?q=${ADDRESS_ENC}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
                <div className="p-5 bg-[#050814] border-t border-[#B8860B]/20 flex items-center justify-end">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${ADDRESS_ENC}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#E5C158] text-sm"
                  >
                    View on Google Maps <ExternalLink size={13} />
                  </a>
                </div>
              </Card>
            </div>

            {/* Arrival tips */}
            <div className="space-y-4">
              <Card
                data-testid="directions-entrance-card"
                className="border border-[#B8860B]/30 bg-[#0B132B] p-6"
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="w-10 h-10 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/40 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#D4AF37]" size={18} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-1">
                      Neighborhood Entrance
                    </p>
                    <p className="text-[#F4F1EA] font-semibold leading-snug">Off FM-230</p>
                  </div>
                </div>
                <p className="text-sm text-[#F4F1EA]/75 leading-relaxed">
                  Turn onto <strong className="text-[#E5C158]">Private Road 8160</strong> from FM-230.
                  You'll come to an <strong className="text-[#E5C158]">iron gate marked "Pineywoods Ranch"</strong> — that's the neighborhood entrance. Pass through and keep going.
                </p>
              </Card>

              <Card
                data-testid="directions-lot-card"
                className="border border-[#B8860B]/30 bg-[#0B132B] p-6"
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="w-10 h-10 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/40 flex items-center justify-center flex-shrink-0">
                    <Route className="text-[#D4AF37]" size={18} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-1">
                      Our Lot
                    </p>
                    <p className="text-[#F4F1EA] font-semibold leading-snug">Watch for our marquee</p>
                  </div>
                </div>
                <p className="text-sm text-[#F4F1EA]/75 leading-relaxed">
                  Once inside Pineywoods Ranch, follow PR 8160 until you see our lit{' '}
                  <strong className="text-[#E5C158]">marquee sign that says "Creek Bottom Ranch"</strong>. Pull in there — the observation deck sits just ahead.
                </p>
              </Card>

              <Card className="border border-[#B8860B]/20 bg-[#050814] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="text-[#D4AF37]" size={16} />
                  <p className="text-xs uppercase tracking-widest text-[#F4F1EA]/60">Arrival window</p>
                </div>
                <p className="text-[#F4F1EA]/85 text-sm leading-relaxed">
                  Check-in: <strong className="text-[#E5C158]">2:00 PM</strong>. If you arrive late or
                  need help finding us, call{' '}
                  <a href="tel:8327140101" className="text-[#D4AF37] hover:text-[#E5C158]">
                    (832) 714-0101
                  </a>
                  .
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Directions from surrounding cities */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-[#050814] via-[#0B132B] to-[#050814] border-t border-[#B8860B]/15">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="uppercase tracking-[0.4em] text-[10px] md:text-xs text-[#D4AF37] mb-3">
              Coming from…
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#F4F1EA] mb-3">
              Driving Directions
            </h2>
            <p className="text-sm md:text-base text-[#F4F1EA]/65 max-w-xl mx-auto">
              Approximate drive times from nearby cities. Tap "Get directions" to open turn-by-turn navigation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {CITIES.map((c) => (
              <Card
                key={c.city}
                data-testid={`directions-city-${c.city.split(',')[0].toLowerCase().replace(/\s+/g, '-')}`}
                className="group border border-[#B8860B]/25 bg-[#0B132B] hover:border-[#B8860B]/55 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-all duration-300 flex flex-col"
              >
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-display text-2xl font-semibold text-[#F4F1EA]">{c.city}</h3>
                      <p className="text-xs uppercase tracking-widest text-[#D4AF37]/80 mt-1">
                        {c.time} · {c.miles}
                      </p>
                    </div>
                    <span className="w-10 h-10 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/40 flex items-center justify-center flex-shrink-0">
                      <Chrome className="text-[#D4AF37]" size={17} />
                    </span>
                  </div>
                  <p className="text-sm text-[#F4F1EA]/75 leading-relaxed mb-5 flex-1">{c.route}</p>
                  <a
                    href={googleMapsDirUrl(c.city)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 self-start px-4 py-2 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/40 text-[#E5C158] hover:bg-[#B8860B] hover:text-[#050814] transition-colors text-sm font-medium"
                  >
                    Get directions <Navigation size={14} />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 border-t border-[#B8860B]/15">
        <div className="container mx-auto px-4 text-center">
          <p className="text-[#F4F1EA]/75 mb-4">
            Still can't find us? Text or call us and we'll walk you in.
          </p>
          <a
            href="tel:8327140101"
            className="inline-flex items-center gap-2 text-2xl font-display text-[#E5C158] hover:text-[#D4AF37]"
          >
            <Navigation size={18} /> (832) 714-0101
          </a>
        </div>
      </section>
    </div>
  );
};

export default Directions;
